import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');
      setTimeout(() => {
        trapFocus(details);
      }, 0);
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    onAnimationEnd(details, () => {
      reset(details);
      if (details === this.refs.details) {
        removeTrapFocus();
        const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
        openDetails.forEach(reset);
      } else {
        setTimeout(() => {
          trapFocus(this.refs.details);
        }, 0);
      }
    });
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    /**
     * @param {AnimationEvent} event
     */
    function removeWillChangeOnAnimationEnd(event) {
      const target = event.target;
      if (target && target instanceof HTMLElement) {
        target.style.setProperty('will-change', 'unset');
        target.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
      }
    }
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}

(function () {
  const TARGET_TEXT = 'delivery fee';

  function hideDeliveryFeeControls() {
    document.querySelectorAll('.cart-items__table-row').forEach((row) => {
      const titleEl = row.querySelector('.cart-items__title');
      if (!titleEl) return;

      if (titleEl.textContent.toLowerCase().includes(TARGET_TEXT)) {
        const qty = row.querySelector('.cart-items__quantity');
        const remove = row.querySelector('.cart-items__remove');

        if (qty) qty.style.display = 'none';
        if (remove) remove.style.display = 'none';
      }
    });
  }

  // Run on load
  document.addEventListener('DOMContentLoaded', hideDeliveryFeeControls);

  // Run when cart updates (drawer refresh, ajax, etc)
  const observer = new MutationObserver(hideDeliveryFeeControls);
  observer.observe(document.body, { childList: true, subtree: true });
})();

(function () {
  const TARGET_TEXT = 'delivery fee';

  async function getCart() {
    const res = await fetch('/cart.js', { credentials: 'same-origin' });
    return await res.json();
  }

  async function removeLineByKey(key) {
    await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ id: key, quantity: 0 }),
    });
  }

  async function cleanupDeliveryFeeIfAlone() {
    try {
      const cart = await getCart();
      if (!cart || !Array.isArray(cart.items)) return;

      // Find delivery fee line(s)
      const feeLines = cart.items.filter((it) =>
        (it.product_title || it.title || '').toLowerCase().includes(TARGET_TEXT)
      );

      if (!feeLines.length) return;

      // Count non-fee items
      const nonFeeCount = cart.items.length - feeLines.length;

      // If cart empty OR only delivery fee remains => remove delivery fee line(s)
      // (cart empty normally means no items, but we handle both cases)
      if (cart.items.length === 0 || nonFeeCount === 0) {
        for (const line of feeLines) {
          await removeLineByKey(line.key);
        }
      }
    } catch (e) {
      console.warn('Delivery fee cleanup failed', e);
    }
  }

  // Run once on load
  document.addEventListener('DOMContentLoaded', cleanupDeliveryFeeIfAlone);

  // Run whenever cart UI changes (drawer/cart ajax updates)
  const observer = new MutationObserver(() => {
    // debounce a bit to avoid spamming
    clearTimeout(window.__dfCleanupTimer);
    window.__dfCleanupTimer = setTimeout(cleanupDeliveryFeeIfAlone, 300);
  });
  observer.observe(document.body, { childList: true, subtree: true });
})();
